import { UserReviewFormType, UserUpdateFormType } from "./user-update.schema";
import { mapToUserReviewRequest, mapToUserUpdateRequest } from "./user-update.mapper";

import { GET_PROFILE } from "../user-detail/user-detail.query";
import { IUserPhoneUpdateRequest, IUserUpdateResponse } from "./user-update.interface";
import { UPDATE_USER } from "./user-update.mutation";
import { useDispatch } from "react-redux";
import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useRefreshProfile } from "../user-detail/user-detail.service";
import { useUserStore } from "../../hooks/redux-hooks/user-store/user.store";
import { userActions } from "../../state/user";

export const useUserUpdate = () => {
  const dispatch = useDispatch();
  const refreshProfile = useRefreshProfile();
  const { profileUser } = useUserStore();
  const [refreshing, setRefreshing] = useState(false);
  const [updateUser, { data, loading, error }] = useMutation<{ updateUser: IUserUpdateResponse }>(UPDATE_USER);

  const userUpdate = (file: File, user: UserUpdateFormType) => {
    const userRequest = mapToUserUpdateRequest(user);

    updateUser({
      variables: { file, user: userRequest },
      refetchQueries: [
        { query: GET_PROFILE },
      ],
    },
    );
  };

  // Phone-only update for users whose profile already exists (EzyVet-created).
  // The route guards derive completeness from the Redux profile, so the fresh
  // profile is stored before this resolves; callers must not navigate on their
  // own, the guards redirect once the profile is complete.
  //
  // `location` is sent although the backend ignores it: its input validator
  // has no `@IsOptional`, so omitting it fails validation.
  const userUpdatePhone = async (phone: string, locationId: string) => {
    const userRequest: IUserPhoneUpdateRequest = { phone: phone.replaceAll("-", ""), location: locationId };

    setRefreshing(true);
    try {
      await updateUser({ variables: { user: userRequest } });

      const freshUser = await refreshProfile();

      if (!freshUser && profileUser) {
        dispatch(userActions.setUserProfile({ ...profileUser, phone: userRequest.phone }));
      }
    } catch {
      // `error` is exposed by useMutation; the field message comes from
      // the global errorLink.
    } finally {
      setRefreshing(false);
    }
  };

  const userReview = (user: UserReviewFormType) => {
    const userRequest = mapToUserReviewRequest(user);

    updateUser({
      variables: { user: userRequest },
    });
  };

  return {
    user: data?.updateUser.user,
    userUpdate,
    userUpdatePhone,
    userReview,
    loading: loading || refreshing,
    error,
  };
};
